import { generatePath } from 'react-router-dom';
import type { RouteParams } from './pathParamsTypes';
import { buildQueryString } from './search';
import type { SearchKeyMap, SearchShape, SearchValues } from './search';

type ParamsArgs<Path extends string> = [RouteParams<Path>] extends [never]
  ? []
  : [params: RouteParams<Path>];

type RouteToArgs<Path extends string, SearchKeys extends string> = [
  SearchKeys,
] extends [never]
  ? ParamsArgs<Path>
  : [...ParamsArgs<Path>, search: SearchShape<SearchKeys>];

type GeneratePathParams<Path extends string> = Parameters<
  typeof generatePath<Path>
>[1];

export interface Route<Path extends string, SearchKeys extends string = never> {
  readonly path: Path;
  readonly searchParams: SearchKeyMap<SearchKeys>;
  to: (...args: RouteToArgs<Path, SearchKeys>) => string;
}

export function createRoute<
  const Path extends string,
  const Keys extends readonly string[] = [],
>(path: Path, options?: { search: Keys }): Route<Path, Keys[number]> {
  const hasParams = /[:*]/.test(path);
  const keys = options?.search ?? [];
  const hasSearch = keys.length > 0;
  const searchParams = Object.fromEntries(
    keys.map((key) => [key, key]),
  ) as SearchKeyMap<Keys[number]>;

  return {
    path,
    searchParams,
    to: (...args) => {
      const params = hasParams ? (args[0] as GeneratePathParams<Path>) : undefined;
      const search = hasSearch ? (args.at(-1) as SearchValues) : undefined;
      const pathname = generatePath(path, params);

      return `${pathname}${buildQueryString(search)}`;
    },
  };
}
